// Comprobante en pantalla del cobro en tienda: folio, qué se pagó, cómo, dónde, quién y el saldo.
import { CheckCircle2 } from 'lucide-react';
import type { SellerPaymentResult } from '@/api/services/loan-credit';
import { Button } from '@/kit';
import { formatDateTimeMx } from '@/lib/dateHelpers';
import { wireLookup } from '@/lib/wireLookup';
import { IN_STORE_METHOD_LABEL } from '@/domains/credit/payments/paymentOrigin';
import { unknownWireLabel } from '@/domains/credit/installments/payments';
import {
    CHARGE_BUTTON,
    CHARGE_KIND_TITLE,
    dueDateLabel,
    money,
    RECEIPT_LABEL,
    RECEIPT_NO_NEXT_INSTALLMENT,
    RECEIPT_UNKNOWN,
} from '../lib/collectionsFormat';

export interface PaymentReceiptProps {
    result: SellerPaymentResult;
    onAnother: () => void;
}

function deviceText(credit: SellerPaymentResult['credit']): string {
    const product = [credit.productBrand, credit.productModel].filter(Boolean).join(' ');
    const imei = credit.imei ? `IMEI ${credit.imei}` : '';
    return [product, imei].filter(Boolean).join(' · ') || RECEIPT_UNKNOWN;
}

function nextInstallmentText(next: SellerPaymentResult['nextInstallment']): string {
    if (!next) return RECEIPT_NO_NEXT_INSTALLMENT;
    return `#${String(next.installmentNumber)} · ${dueDateLabel(next.dueDate ?? undefined)} · ${money(next.amountCents)}`;
}

export function PaymentReceipt({ result, onAnother }: PaymentReceiptProps) {
    const rows: [string, string][] = [
        [RECEIPT_LABEL.amount, money(result.amountCents)],
        [
            RECEIPT_LABEL.kind,
            wireLookup(CHARGE_KIND_TITLE, result.type) ?? unknownWireLabel(result.type),
        ],
        [
            RECEIPT_LABEL.method,
            wireLookup(IN_STORE_METHOD_LABEL, result.method) ?? unknownWireLabel(result.method),
        ],
        [RECEIPT_LABEL.appliedAt, formatDateTimeMx(result.appliedAt)],
        [RECEIPT_LABEL.store, result.collectedAtStore.name ?? RECEIPT_UNKNOWN],
        [RECEIPT_LABEL.seller, result.collectedBySeller.name ?? RECEIPT_UNKNOWN],
        [RECEIPT_LABEL.balanceAfter, money(result.outstandingBalanceAfterCents)],
        [RECEIPT_LABEL.nextInstallment, nextInstallmentText(result.nextInstallment)],
        [RECEIPT_LABEL.device, deviceText(result.credit)],
    ];
    return (
        <section
            aria-label={RECEIPT_LABEL.title}
            className="rounded-xl border-2 border-success/40 bg-card px-4 py-3.5"
        >
            <div className="mb-2.5 flex items-center gap-2 text-success">
                <CheckCircle2 aria-hidden className="size-5" />
                <h3 className="text-sm font-extrabold">{RECEIPT_LABEL.title}</h3>
            </div>
            <div className="mb-3 text-[11px] text-muted-foreground">
                {RECEIPT_LABEL.folio}{' '}
                <span className="font-mono text-[13px] font-bold text-foreground">
                    {result.receipt}
                </span>
            </div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[12px]">
                {rows.map(([label, value]) => (
                    <div key={label} className="contents">
                        <dt className="text-muted-foreground">{label}</dt>
                        <dd className="text-right font-semibold tabular-nums">{value}</dd>
                    </div>
                ))}
            </dl>
            <Button variant="outline" size="sm" className="mt-3" onClick={onAnother}>
                {CHARGE_BUTTON.another}
            </Button>
        </section>
    );
}
